/**
 * BoxModelEditor — 4-side padding/margin editor with link toggle.
 * Single responsibility: editing top/right/bottom/left numeric values together or independently.
 */

import React, { useEffect, useState } from 'react';
import { NUMBER_INPUT_NO_NATIVE_SPIN_CLASS, ensureNoNativeSpinStyle } from './noNativeSpin';

export interface BoxModelEditorProps {
  label: string;
  top: number | undefined;
  right: number | undefined;
  bottom: number | undefined;
  left: number | undefined;
  onChange: (sides: { top?: number; right?: number; bottom?: number; left?: number }) => void;
  isDark: boolean;
}

/**
 * BoxModelEditor component — link icon toggle + 1 or 4 inputs.
 */
export const BoxModelEditor: React.FC<BoxModelEditorProps> = ({
  label,
  top,
  right,
  bottom,
  left,
  onChange,
  isDark,
}) => {
  const [linked, setLinked] = useState(true);

  useEffect(() => { ensureNoNativeSpinStyle(); }, []);

  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';
  const accent = isDark ? '#22c55e' : '#16a34a';

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
  };

  const linkBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${linked ? accent : borderColor}`,
    borderRadius: 4,
    cursor: 'pointer',
    color: linked ? accent : textSecondary,
    fontSize: 12,
    padding: '1px 6px',
    lineHeight: 1.4,
  };

  const miniInputStyle: React.CSSProperties = {
    width: 48,
    padding: '3px 4px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    textAlign: 'center',
    boxSizing: 'border-box',
  };

  const microLabelStyle: React.CSSProperties = {
    fontSize: 10,
    color: textSecondary,
    textAlign: 'center',
    display: 'block',
    marginTop: 2,
  };

  const linkedInputStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    padding: '4px 8px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    textAlign: 'right',
    boxSizing: 'border-box',
  };

  const linkedRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  const stepperStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${borderColor}`,
  };

  const stepperBtnStyle: React.CSSProperties = {
    background: inputBg,
    border: 'none',
    color: textSecondary,
    cursor: 'pointer',
    padding: 0,
    width: 14,
    height: 11,
    fontSize: 8,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const parseVal = (raw: string): number | undefined => {
    const n = parseFloat(raw);
    return isNaN(n) ? undefined : n;
  };

  // Representative value when linked (use top as the single value)
  const linkedVal = top !== undefined ? top : (right !== undefined ? right : (bottom !== undefined ? bottom : left));

  const bumpLinked = (delta: number) => {
    const v = (linkedVal ?? 0) + delta;
    onChange({ top: v, right: v, bottom: v, left: v });
  };

  if (linked) {
    return (
      <div>
        <div style={headerStyle}>
          <span style={labelStyle}>{label}</span>
          <button
            type="button"
            style={linkBtnStyle}
            onClick={() => setLinked(false)}
            aria-label={`Unlink ${label} sides`}
            title="All sides linked — click to edit individually"
          >
            🔗
          </button>
        </div>
        <div style={linkedRowStyle}>
          <input
            type="number"
            className={NUMBER_INPUT_NO_NATIVE_SPIN_CLASS}
            value={linkedVal ?? ''}
            onChange={(e) => {
              const v = parseVal(e.target.value);
              onChange({ top: v, right: v, bottom: v, left: v });
            }}
            placeholder="—"
            style={linkedInputStyle}
            aria-label={`${label} all sides`}
          />
          <span style={stepperStyle}>
            <button
              type="button"
              style={stepperBtnStyle}
              onClick={() => bumpLinked(1)}
              aria-label={`Increase ${label}`}
              title="Increase"
            >
              ▲
            </button>
            <button
              type="button"
              style={{ ...stepperBtnStyle, borderTop: `1px solid ${borderColor}` }}
              onClick={() => bumpLinked(-1)}
              aria-label={`Decrease ${label}`}
              title="Decrease"
            >
              ▼
            </button>
          </span>
        </div>
      </div>
    );
  }

  // Independent mode: cross/grid layout
  const IndividualInput = ({
    sideLabel,
    val,
    onSideChange,
  }: {
    sideLabel: string;
    val: number | undefined;
    onSideChange: (v: number | undefined) => void;
  }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <input
        type="number"
        className={NUMBER_INPUT_NO_NATIVE_SPIN_CLASS}
        value={val ?? ''}
        onChange={(e) => onSideChange(parseVal(e.target.value))}
        placeholder="—"
        style={miniInputStyle}
        aria-label={`${label} ${sideLabel}`}
      />
      <span style={microLabelStyle}>{sideLabel}</span>
    </div>
  );

  return (
    <div>
      <div style={headerStyle}>
        <span style={labelStyle}>{label}</span>
        <button
          type="button"
          style={linkBtnStyle}
          onClick={() => setLinked(true)}
          aria-label={`Link ${label} sides`}
          title="Sides independent — click to link all"
        >
          🔓
        </button>
      </div>
      {/* Cross layout: T centered top, R right, B centered bottom, L left */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 48px 1fr', gridTemplateRows: 'auto auto auto', gap: 4, alignItems: 'center' }}>
        {/* Row 0: Top centered */}
        <div />
        <IndividualInput sideLabel="T" val={top} onSideChange={(v) => onChange({ top: v, right, bottom, left })} />
        <div />
        {/* Row 1: Left | (spacer) | Right */}
        <IndividualInput sideLabel="L" val={left} onSideChange={(v) => onChange({ top, right, bottom, left: v })} />
        <div style={{ textAlign: 'center', fontSize: 10, color: textSecondary }}>↕</div>
        <IndividualInput sideLabel="R" val={right} onSideChange={(v) => onChange({ top, right: v, bottom, left })} />
        {/* Row 2: Bottom centered */}
        <div />
        <IndividualInput sideLabel="B" val={bottom} onSideChange={(v) => onChange({ top, right, bottom: v, left })} />
        <div />
      </div>
    </div>
  );
};

BoxModelEditor.displayName = 'BoxModelEditor';
